import { useState } from 'react';
import { Images, X } from 'lucide-react';
import {
  DECOR_LIBRARY_CATEGORY_SPECS,
  pickOf,
  withPick,
  withoutCategoryFields,
  type DecorLibraryCategory,
  type DecorLook,
  type DecorPicks,
} from '@peles/shared';
import { useDecorLibrary } from '../../hooks/useDecorLibrary';
import { DecorLibraryPickerModal } from './DecorLibraryPickerModal';

export interface DecorPicksListProps {
  categories: readonly DecorLibraryCategory[];
  /** The look being set — its picks are this level's choices. */
  look: DecorLook;
  onChange: (next: DecorLook) => void;
  /** What is in force without a choice here: the event's or the plan's picks. */
  inheritedPicks?: DecorPicks;
  /** "Plan’s", "Event’s". */
  inheritedLabel: string;
  /** "the whole event", "Table 4". */
  forWhat: string;
  /** Narrow rows for the floor plan's side panel. */
  compact?: boolean;
}

/**
 * One row per category: the piece chosen here (its cover and name), or what
 * is in force without a choice, with Choose / Change opening the library
 * filtered to that category, and × going back to what is inherited.
 * Choosing a piece clears the same things set by hand at this level, so the
 * piece is what shows.
 */
export function DecorPicksList({
  categories,
  look,
  onChange,
  inheritedPicks = {},
  inheritedLabel,
  forWhat,
  compact = false,
}: DecorPicksListProps) {
  const library = useDecorLibrary();
  const [choosing, setChoosing] = useState<DecorLibraryCategory | null>(null);
  const coverOf = (itemId: string) =>
    library.data?.find((i) => i.id === itemId)?.pictures.find((p) => p.url)?.url ?? null;

  return (
    <>
      <ul className={compact ? 'space-y-1.5' : 'grid gap-2 sm:grid-cols-2'}>
        {categories.map((category) => {
          const own = look.picks?.[category];
          const inherited = inheritedPicks[category];
          const shown = own ?? inherited;
          const cover = shown ? coverOf(shown.itemId) : null;
          const label = DECOR_LIBRARY_CATEGORY_SPECS[category].label;
          return (
            <li
              key={category}
              className={`flex items-center gap-2 rounded-lg border ${
                own ? 'border-primary-200 bg-primary-50/40' : 'border-gray-200 bg-white'
              } ${compact ? 'p-1.5' : 'p-2'}`}
            >
              <span
                className={`flex shrink-0 items-center justify-center overflow-hidden rounded-md bg-gray-100 ${
                  compact ? 'h-10 w-10' : 'h-14 w-14'
                }`}
              >
                {cover ? (
                  <img
                    src={cover}
                    alt=""
                    className={`h-full w-full object-cover ${own ? '' : 'opacity-60'}`}
                  />
                ) : (
                  <Images className="h-5 w-5 text-gray-400" aria-hidden />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium text-gray-500">{label}</span>
                <span
                  className={`block truncate text-sm ${own ? 'font-medium text-gray-900' : 'italic text-gray-500'}`}
                >
                  {own
                    ? own.name
                    : inherited
                      ? `${inheritedLabel}: ${inherited.name}`
                      : 'Not chosen'}
                </span>
              </span>
              <button
                type="button"
                onClick={() => setChoosing(category)}
                aria-label={`${own ? 'Change' : 'Choose'} ${label.toLowerCase()} for ${forWhat}`}
                className="min-h-11 sm:min-h-8 shrink-0 rounded-md border border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                {own ? 'Change' : 'Choose'}
              </button>
              {own && (
                <button
                  type="button"
                  onClick={() => onChange(withPick(look, category, null))}
                  aria-label={`Clear ${label.toLowerCase()} for ${forWhat}`}
                  className="flex min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {choosing && (
        <DecorLibraryPickerModal
          open
          onClose={() => setChoosing(null)}
          category={choosing}
          forWhat={forWhat}
          selectedId={look.picks?.[choosing]?.itemId ?? null}
          onPick={(item) =>
            onChange(withPick(withoutCategoryFields(look, choosing), choosing, pickOf(item)))
          }
        />
      )}
    </>
  );
}
